import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import './estilodash.css';

function formatMoney(value) {
  return Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('pt-BR');
}

function formatMesReferencia(value) {
  if (!value || !String(value).includes('-')) return value || '—';
  const [ano, mes] = String(value).split('-');
  return `${mes}/${ano}`;
}

export default function ListarPagamentos() {
  const [pagamentos, setPagamentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [status, setStatus] = useState('todos');
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = async () => {
    try {
      setLoading(true);
      setErro('');

      const [p, c] = await Promise.all([
        api.get('/api/pagamentos'),
        api.get('/api/clientes'),
      ]);

      setPagamentos(p.data || []);
      setClientes(c.data || []);
    } catch (err) {
      console.error('Erro ao carregar pagamentos:', err);
      setErro('Erro ao carregar pagamentos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar().catch(console.error);
  }, []);

  const linhas = useMemo(() => {
    const termo = busca.toLowerCase();

    return pagamentos
      .map((p) => ({
        ...p,
        cliente: clientes.find(
          (c) => Number(c.id) === Number(p.cliente_id)
        ),
      }))
      .filter((p) => {
        const okCliente =
          !clienteId || String(p.cliente_id) === String(clienteId);

        const okStatus =
          status === 'todos' ||
          String(p.status || '').toLowerCase() === status.toLowerCase();

        const okBusca =
          !termo ||
          String(p.id || '').toLowerCase().includes(termo) ||
          String(p.cliente?.nome || '').toLowerCase().includes(termo) ||
          String(p.cliente?.email || '').toLowerCase().includes(termo) ||
          String(p.mes_referencia || '').toLowerCase().includes(termo) ||
          String(p.metodo_pagamento || '').toLowerCase().includes(termo) ||
          String(p.status || '').toLowerCase().includes(termo) ||
          String(p.descricao || '').toLowerCase().includes(termo) ||
          String(p.origem_cobranca || '').toLowerCase().includes(termo);

        return okCliente && okStatus && okBusca;
      })
      .sort((a, b) => {
        const mesA = String(a.mes_referencia || '');
        const mesB = String(b.mes_referencia || '');
        if (mesA !== mesB) return mesB.localeCompare(mesA);

        const dataA = new Date(a.data_pagamento || a.created_on || 0).getTime();
        const dataB = new Date(b.data_pagamento || b.created_on || 0).getTime();
        return dataB - dataA;
      });
  }, [pagamentos, clientes, clienteId, status, busca]);

  return (
    <div className="pagamentos-container">
      <h2>Pagamentos</h2>
      <p>
        Histórico financeiro com os dados mais importantes do pagamento e a
        descrição completa do que foi cobrado.
      </p>

      {erro && (
        <div
          style={{
            background: '#fdecec',
            color: '#7f1d1d',
            border: '1px solid #f5b7b7',
            borderRadius: 6,
            padding: 10,
            marginBottom: 12,
          }}
        >
          {erro}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr',
          gap: 12,
          marginBottom: 16,
          alignItems: 'end',
        }}
      >
        <div style={{ width: '100%' }}>
          <label style={{ display: 'block', width: '100%', marginBottom: 0 }}>
            Buscar
          </label>
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Cliente, mês, status, método ou descrição..."
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ width: '100%' }}>
          <label style={{ display: 'block', width: '100%', marginBottom: 0 }}>
            Cliente
          </label>
          <select
            value={clienteId}
            onChange={(e) => setClienteId(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">Todos</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.id} - {c.nome}
              </option>
            ))}
          </select>
        </div>

        <div style={{ width: '100%' }}>
          <label style={{ display: 'block', width: '100%', marginBottom: 0 }}>
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="todos">Todos</option>
            <option value="Aguardando pagamento">Aguardando pagamento</option>
            <option value="Pago">Pago</option>
            <option value="Isento">Isento</option>
            <option value="Cancelado">Cancelado</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p>Carregando pagamentos...</p>
      ) : (
        <table className="clientes-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Cliente</th>
              <th>Mês</th>
              <th>Data</th>
              <th>Valor</th>
              <th>Método</th>
              <th>Status</th>
              <th>Origem</th>
              <th>Descrição</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td style={{ textAlign: 'left' }}>
                  <strong>{p.cliente?.nome || p.cliente_id}</strong>
                  <br />
                  <small>{p.cliente?.email || '—'}</small>
                </td>
                <td>{formatMesReferencia(p.mes_referencia)}</td>
                <td>{formatDate(p.data_pagamento)}</td>
                <td>
                  <strong>R$ {formatMoney(p.valor)}</strong>
                </td>
                <td>{p.metodo_pagamento || '—'}</td>
                <td>
                  <span
                    className={
                      String(p.status || '').toLowerCase() === 'pago'
                        ? 'badge badge-ativo'
                        : String(p.status || '').toLowerCase() === 'isento'
                        ? 'badge badge-isento'
                        : String(p.status || '').toLowerCase() === 'cancelado'
                        ? 'badge badge-inativo'
                        : 'badge badge-normal'
                    }
                  >
                    {p.status || '—'}
                  </span>
                </td>
                <td>{p.origem_cobranca || '—'}</td>
                <td style={{ textAlign: 'left', whiteSpace: 'normal' }}>
                  {p.descricao || '—'}
                </td>
              </tr>
            ))}

            {linhas.length === 0 && (
              <tr>
                <td colSpan="9">Nenhum pagamento encontrado.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}